import React, { useState, useEffect, useCallback } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const POLLING_INTERVAL = 15000;

// Reusable Gradient Badge for Roles
const RoleBadge = ({ roleName }) => {
  const normalized = (roleName || "").toLowerCase();
  let style = {
    padding: "6px 12px",
    borderRadius: "12px",
    fontWeight: 600,
    fontSize: "0.8rem",
    display: "inline-block",
    textTransform: "capitalize",
    color: "#fff",
    boxShadow: "0 0 8px rgba(0,0,0,0.3)",
  };

  const colorMap = {
    admin: "linear-gradient(135deg, #DC2626, #EF4444)",
    manager: "linear-gradient(135deg, #0EA5E9, #38BDF8)",
    agent: "linear-gradient(135deg, #16A34A, #22C55E)",
    user: "linear-gradient(135deg, #6B7280, #9CA3AF)",
  };

  style.background = colorMap[normalized] || "linear-gradient(135deg, #9CA3AF, #D1D5DB)";
  style.color = normalized === "user" ? "#1E293B" : "#fff";
  style.boxShadow = style.background.includes("34,197,94")
    ? "0 0 10px rgba(34,197,94,0.5)"
    : style.background.includes("14,165,233")
    ? "0 0 10px rgba(14,165,233,0.5)"
    : style.background.includes("239,68,68")
    ? "0 0 10px rgba(239,68,68,0.5)"
    : "0 0 10px rgba(156,163,175,0.5)";

  return <span style={style}>{roleName || "Unknown"}</span>;
};

const AdminCreatePage = () => {
  const [roles, setRoles] = useState([]);
  const [formData, setFormData] = useState({
    user_id: "",
    name: "",
    email: "",
    password: "",
    phone_number: "",
    permissions_level: "",
  });
  const [loading, setLoading] = useState(true);
  const [isPolling, setIsPolling] = useState(false);
  const [error, setError] = useState(null);
  const API_BASE_URL =
    process.env.REACT_APP_API_BASE_URL || "https://installmentzone.com/api";

  const fetchRoles = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/roles/fetch_roles.php`);
      const result = await response.json();
      if (result.success) {
        setRoles(result.data || []);
      } else {
        throw new Error(result.message || "Failed to load roles");
      }
    } catch (err) {
      setError("Failed to load roles.");
      toast.error("Error loading roles.");
    }
  }, [API_BASE_URL]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.user_id || !formData.name || !formData.email || !formData.password || !formData.permissions_level) {
      toast.error("All fields are required.");
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/admins/create_admin.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      const result = await response.json();
      if (result.success) {
        toast.success(result.message || "Admin created successfully.");
        setFormData({
          user_id: "",
          name: "",
          email: "",
          password: "",
          phone_number: "",
          permissions_level: "",
        });
        fetchRoles(); // Refresh roles in case new one was created
      } else {
        throw new Error(result.message || "Failed to create admin");
      }
    } catch (err) {
      toast.error(err.message || "Failed to create admin.");
    }
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await fetchRoles();
      setLoading(false);
    };
    load();
  }, [fetchRoles]);

  useEffect(() => {
    if (loading || error) return;
    const interval = setInterval(() => {
      if (!isPolling) {
        setIsPolling(true);
        fetchRoles().finally(() => setIsPolling(false));
      }
    }, POLLING_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchRoles, loading, error, isPolling]);

  return (
    <div
      style={{
        minHeight: "100vh",
        padding: "40px 20px",
        background: "linear-gradient(135deg, #1E3A8A, #3B82F6, #60A5FA)",
        fontFamily: "Segoe UI, sans-serif",
        color: "#fff",
        overflowY: "auto",
      }}
    >
      <ToastContainer theme="dark" />

      <div
        style={{
          background: "rgba(255,255,255,0.08)",
          padding: "30px",
          borderRadius: "15px",
          maxWidth: "600px",
          margin: "0 auto",
          boxShadow: "0 6px 18px rgba(0,0,0,0.3)",
          backdropFilter: "blur(8px)",
        }}
      >
        <div style={{ textAlign: "center", marginBottom: "25px" }}>
          <h1 style={{ fontSize: "28px", fontWeight: "600" }}>
            Create Admin
          </h1>
          <p style={{ color: "#E0E7FF" }}>
            Add a new admin user to the system.
          </p>
          {isPolling && (
            <small style={{ color: "#93C5FD" }}>Updating roles...</small>
          )}
        </div>

        {error && (
          <div
            style={{
              background: "rgba(255,255,255,0.1)",
              border: "1px solid #F87171",
              borderRadius: "10px",
              padding: "15px",
              color: "#FCA5A5",
              textAlign: "center",
              marginBottom: "20px",
            }}
          >
            <strong>Error:</strong> {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: "18px" }}>
            <label style={{ display: "block", marginBottom: "6px", fontWeight: "500", color: "#E0E7FF" }}>
              User ID
            </label>
            <input
              type="text"
              name="user_id"
              value={formData.user_id}
              onChange={handleInputChange}
              required
              placeholder="e.g. admin001"
              style={{
                width: "100%",
                padding: "12px 14px",
                borderRadius: "8px",
                border: "none",
                background: "rgba(255,255,255,0.9)",
                color: "#1E293B",
                fontSize: "0.95rem",
              }}
            />
          </div>

          <div style={{ marginBottom: "18px" }}>
            <label style={{ display: "block", marginBottom: "6px", fontWeight: "500", color: "#E0E7FF" }}>
              Full Name
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              placeholder="John Doe"
              style={{
                width: "100%",
                padding: "12px 14px",
                borderRadius: "8px",
                border: "none",
                background: "rgba(255,255,255,0.9)",
                color: "#1E293B",
                fontSize: "0.95rem",
              }}
            />
          </div>

          <div style={{ marginBottom: "18px" }}>
            <label style={{ display: "block", marginBottom: "6px", fontWeight: "500", color: "#E0E7FF" }}>
              Email Address
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              required
              placeholder="admin@example.com"
              style={{
                width: "100%",
                padding: "12px 14px",
                borderRadius: "8px",
                border: "none",
                background: "rgba(255,255,255,0.9)",
                color: "#1E293B",
                fontSize: "0.95rem",
              }}
            />
          </div>

          <div style={{ marginBottom: "18px" }}>
            <label style={{ display: "block", marginBottom: "6px", fontWeight: "500", color: "#E0E7FF" }}>
              Password
            </label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              required
              placeholder="••••••••"
              style={{
                width: "100%",
                padding: "12px 14px",
                borderRadius: "8px",
                border: "none",
                background: "rgba(255,255,255,0.9)",
                color: "#1E293B",
                fontSize: "0.95rem",
              }}
            />
          </div>

          <div style={{ marginBottom: "18px" }}>
            <label style={{ display: "block", marginBottom: "6px", fontWeight: "500", color: "#E0E7FF" }}>
              Phone Number
            </label>
            <input
              type="tel"
              name="phone_number"
              value={formData.phone_number}
              onChange={handleInputChange}
              required
              placeholder="+1234567890"
              style={{
                width: "100%",
                padding: "12px 14px",
                borderRadius: "8px",
                border: "none",
                background: "rgba(255,255,255,0.9)",
                color: "#1E293B",
                fontSize: "0.95rem",
              }}
            />
          </div>

          <div style={{ marginBottom: "24px" }}>
            <label style={{ display: "block", marginBottom: "6px", fontWeight: "500", color: "#E0E7FF" }}>
              Assign Role
            </label>
            <select
              name="permissions_level"
              value={formData.permissions_level}
              onChange={handleInputChange}
              required
              style={{
                width: "100%",
                padding: "12px 14px",
                borderRadius: "8px",
                border: "none",
                background: "rgba(255,255,255,0.9)",
                color: "#1E293B",
                fontSize: "0.95rem",
              }}
            >
              <option value="">-- Select Role --</option>
              {roles.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.name}
                </option>
              ))}
            </select>
            {formData.permissions_level && (
              <div style={{ marginTop: "8px", textAlign: "center" }}>
                <RoleBadge roleName={roles.find(r => r.id === formData.permissions_level)?.name || ""} />
              </div>
            )}
          </div>

          <button
            type="submit"
            style={{
              width: "100%",
              background: "#16A34A",
              color: "#fff",
              border: "none",
              borderRadius: "8px",
              padding: "14px 20px",
              fontWeight: "600",
              fontSize: "1rem",
              cursor: "pointer",
              transition: "background 0.2s ease",
            }}
            onMouseEnter={(e) => e.target.style.background = "#15803D"}
            onMouseLeave={(e) => e.target.style.background = "#16A34A"}
          >
            Create Admin
          </button>
        </form>

        {/* Optional: Show loading skeleton */}
        {loading && (
          <div style={{ marginTop: "30px" }}>
            <div style={{ height: "14px", background: "#CBD5E1", borderRadius: "4px", width: "80%", margin: "10px auto" }}></div>
            <div style={{ height: "14px", background: "#CBD5E1", borderRadius: "4px", width: "60%", margin: "10px auto" }}></div>
            <div style={{ height: "40px", background: "#CBD5E1", borderRadius: "8px", width: "100%", margin: "10px auto" }}></div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminCreatePage;